import { useEffect, useState } from "react";
import { Box, Typography, Dialog } from "@mui/material";
import { T } from "./tokens";
import { Pill, LocalOnlyBadge } from "./ui";
import { sfVisitUrl, toISODate } from "./data";
import type { SfVisit } from "./data";
import { scoreVisit, fmtPoints, VISIT_TYPE_LABEL, UNRATED_LABEL } from "./points";
import { usePointsConfig } from "./pointsConfig";

/**
 * Visit detail modal — opened from a Schedule chip. Shows the full record and deep
 * links to Salesforce. PROTOTYPE edit mode: changes apply to this board only.
 */

// Shows the points for this visit and WHY. A score nobody can trace to a rule is
// not auditable — the whole complaint about the spreadsheet this replaces.
const PointsRows = ({ visit }: { visit: SfVisit }) => {
    const { config, showPoints } = usePointsConfig();
    if (!showPoints) return null;
    const score = scoreVisit(
        {
            subActivity: visit.subActivity,
            name: visit.name,
            visitCategory: visit.visitCategory,
            industry: visit.industry,
            fraRiskLevel: visit.fraRiskLevel,
            highRiskFra: visit.highRiskFra,
        },
        config,
        // Every other surface assumes on site when the flag is unset; without this
        // the modal says "not scored" for a visit its own chip scores.
        { assumeOnSiteIfUnset: true },
    );

    return (
        <>
            <Row
                label="Visit type"
                value={`${VISIT_TYPE_LABEL[score.type]}${
                    score.type === "FACT_FIND" ? " (from the visit name)" : ""
                }`}
            />
            <Row
                label="Points"
                value={score.points == null ? "not scored" : `${fmtPoints(score.points)} · ${score.rule}`}
            />
            {score.unrated && <Row label="Why no points" value={UNRATED_LABEL[score.unrated]} />}
            {score.modeAssumed && (
                <Row
                    label="Assumed"
                    value="No On Site / Remote flag on this visit in Salesforce, so it is counted as on site"
                />
            )}
            {score.mayBeUnderScored && (
                <Row
                    label="Caveat"
                    value="A fire assessment at a High Risk site scores a full day. No fire-risk field exists yet, so this may be too low."
                />
            )}
        </>
    );
};

const fmtTime = (iso?: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};
const fmtDate = (iso?: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
};
const fmtDur = (mins?: number | null) => {
    if (mins == null || isNaN(mins) || mins <= 0) return "";
    const m = Math.round(mins);
    return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};
const statusColours = (s?: string): [string, string] => {
    switch ((s || "").toLowerCase()) {
        case "completed": return [T.greenTint, T.green];
        case "booked": return [T.purpleTint, T.purpleDeep];
        case "closed": return ["#F1F3F9", T.muted];
        case "automation proposal": return [T.amberTint, T.amber];
        default: return ["#F1F3F9", T.muted];
    }
};

const Row = ({ label, value }: { label: string; value?: string | number | null }) => {
    if (value == null || value === "") return null;
    return (
        <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: "10px", padding: "7px 0", borderBottom: `1px solid ${T.borderSoft}` }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: T.muted2, alignSelf: "center" }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: "13.5px", color: T.ink2 }}>{value}</Typography>
        </Box>
    );
};

const inputSx: React.CSSProperties = {
    border: "1px solid #DEE2EF",
    borderRadius: "7px",
    padding: "7px 9px",
    fontFamily: "'Nunito', 'Segoe UI', sans-serif",
    fontSize: "13px",
    color: "#150850",
    width: "100%",
    boxSizing: "border-box",
};

const VisitModal = ({
    visit,
    edited,
    onClose,
    onSave,
}: {
    visit: SfVisit | null;
    edited: boolean;
    onClose: () => void;
    onSave: (id: string, patch: Partial<SfVisit>) => void;
}) => {
    const [editing, setEditing] = useState(false);
    const [date, setDate] = useState("");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [status, setStatus] = useState("Booked");

    useEffect(() => {
        setEditing(false);
        if (visit?.visitStartDate) {
            const s = new Date(visit.visitStartDate);
            setDate(toISODate(s));
            // No literal fallbacks: an empty field shows Salesforce has no window,
            // rather than asserting a full 09:00-17:00 day that was never booked.
            setStart(fmtTime(visit.visitStartDate) || "");
            setEnd(fmtTime(visit.visitEndDate) || "");
        }
        setStatus(visit?.status || "Booked");
    }, [visit?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!visit) return null;
    const [bg, fg] = statusColours(visit.status);

    const save = () => {
        const startISO = new Date(`${date}T${start}:00`);
        const endISO = new Date(`${date}T${end}:00`);
        onSave(visit.id, {
            visitStartDate: isNaN(startISO.getTime()) ? visit.visitStartDate : startISO.toISOString(),
            visitEndDate: isNaN(endISO.getTime()) ? visit.visitEndDate : endISO.toISOString(),
            status,
        });
        onClose();
    };

    const dur =
        visit.attendanceMins ??
        (visit.actualStartTime && visit.actualEndTime
            ? (new Date(visit.actualEndTime).getTime() - new Date(visit.actualStartTime).getTime()) / 60000
            : null);

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: "14px", fontFamily: T.font, background: T.card } }}
        >
            <Box sx={{ padding: "20px 24px", fontFamily: T.font, color: T.ink2 }}>
                {/* header */}
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", mb: "6px" }}>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography sx={{ fontSize: "18px", fontWeight: 800, color: T.ink2 }}>
                            {visit.accountName || "Unnamed account"}
                        </Typography>
                        <Typography sx={{ fontSize: "12.5px", color: T.muted }}>
                            {[visit.name, visit.subActivity || visit.description, visit.allocatedToName].filter(Boolean).join(" · ")}
                        </Typography>
                    </Box>
                    <Pill bg={bg} fg={fg}>{visit.status || "—"}</Pill>
                    {edited && <Pill bg={T.amberTint} fg={T.amber}>✎ edited locally</Pill>}
                </Box>

                {!editing ? (
                    <>
                        <Box sx={{ mt: "8px" }}>
                            <Row label="Description" value={visit.description} />
                            <Row label="Date" value={fmtDate(visit.visitStartDate)} />
                            <Row label="Booked window" value={visit.visitStartDate ? `${fmtTime(visit.visitStartDate)} – ${fmtTime(visit.visitEndDate)}` : null} />
                            <Row
                                label="Actual on site"
                                value={visit.actualStartTime ? `${fmtTime(visit.actualStartTime)} – ${fmtTime(visit.actualEndTime)}${dur ? ` · ${fmtDur(dur)}` : ""}` : null}
                            />
                            <Row
                                label="Travel"
                                value={
                                    visit.travelStartTime
                                        ? `out ${fmtTime(visit.travelStartTime)} → ${fmtTime(visit.actualStartTime)} · back ${fmtTime(visit.actualEndTime)} → ${fmtTime(visit.travelEndTime)}`
                                        : null
                                }
                            />
                            <Row label="Write-up" value={visit.writeUpTimeMinutes ? fmtDur(Number(visit.writeUpTimeMinutes)) : null} />
                            <Row label="Category" value={visit.visitCategory} />
                            <Row label="Industry" value={visit.industry || null} />
                            <PointsRows visit={visit} />
                            <Row label="Priority" value={visit.priorityLevel as any} />
                            <Row label="Audit number" value={visit.auditNumber} />
                            <Row label="Booked on" value={visit.bookedOn ? fmtDate(visit.bookedOn) : null} />
                            <Row label="Last visit" value={visit.lastVisitDate ? fmtDate(visit.lastVisitDate) : null} />
                            <Row label="Notes" value={visit.notes} />
                            <Row label="Comments" value={visit.additionalComments} />
                        </Box>

                        {/* actions */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "18px", flexWrap: "wrap" }}>
                            <Box
                                component="a"
                                href={sfVisitUrl(visit.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ padding: "9px 14px", borderRadius: "8px", background: T.ink, color: "#fff", fontSize: "12.5px", fontWeight: 700, textDecoration: "none", "&:hover": { background: T.indigoDeep } }}
                            >
                                Open in Salesforce ↗
                            </Box>
                            <Box
                                component="button"
                                onClick={() => setEditing(true)}
                                sx={{ padding: "9px 14px", borderRadius: "8px", background: T.card, border: `1px solid ${T.border}`, color: T.ink2, fontSize: "12.5px", fontWeight: 700, cursor: "pointer", fontFamily: T.font, "&:hover": { background: T.rowTint } }}
                            >
                                ✎ Edit booking
                            </Box>
                            <Box sx={{ marginLeft: "auto" }}>
                                <Box
                                    component="button"
                                    onClick={onClose}
                                    sx={{ padding: "9px 14px", borderRadius: "8px", background: T.card, border: `1px solid ${T.border}`, color: T.muted, fontSize: "12.5px", fontWeight: 700, cursor: "pointer", fontFamily: T.font, "&:hover": { background: T.rowTint } }}
                                >
                                    Close
                                </Box>
                            </Box>
                        </Box>
                        <Typography sx={{ fontSize: "11px", color: T.muted2, mt: "12px" }}>
                            Salesforce is the source of truth — this view reads from it.
                        </Typography>
                    </>
                ) : (
                    <>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "10px", mb: "8px" }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 800, color: T.ink2 }}>Edit booking</Typography>
                            <LocalOnlyBadge title="Applies to this board only; nothing is written to Salesforce" />
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                            <Box sx={{ gridColumn: "1 / -1" }}>
                                <Typography sx={{ fontSize: "10.5px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: T.muted2, mb: "3px" }}>Date</Typography>
                                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputSx} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: "10.5px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: T.muted2, mb: "3px" }}>Start</Typography>
                                <input type="time" value={start} onChange={(e) => setStart(e.target.value)} style={inputSx} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: "10.5px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: T.muted2, mb: "3px" }}>End</Typography>
                                <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} style={inputSx} />
                            </Box>
                            <Box sx={{ gridColumn: "1 / -1" }}>
                                <Typography sx={{ fontSize: "10.5px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: T.muted2, mb: "3px" }}>Status</Typography>
                                <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputSx}>
                                    {["Booked", "Completed", "Closed", "Automation Proposal"].map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", gap: "10px", mt: "16px" }}>
                            <Box
                                component="button"
                                onClick={save}
                                sx={{ padding: "9px 16px", borderRadius: "8px", background: T.ink, border: "none", color: "#fff", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", fontFamily: T.font, "&:hover": { background: T.indigoDeep } }}
                            >
                                Apply to board
                            </Box>
                            <Box
                                component="button"
                                onClick={() => setEditing(false)}
                                sx={{ padding: "9px 14px", borderRadius: "8px", background: T.card, border: `1px solid ${T.border}`, color: T.muted, fontSize: "12.5px", fontWeight: 700, cursor: "pointer", fontFamily: T.font, "&:hover": { background: T.rowTint } }}
                            >
                                Cancel
                            </Box>
                        </Box>
                        <Typography sx={{ fontSize: "11px", color: T.muted2, mt: "12px" }}>
                            Prototype only — changes apply to this board and are lost on refresh. Real edits happen in
                            Salesforce (the engine will write back once it exists).
                        </Typography>
                    </>
                )}
            </Box>
        </Dialog>
    );
};

export default VisitModal;
